import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box, Grid, Typography, TextField, Button, Table, TableBody, TableCell,
  TableHead, TableRow, Stack, IconButton, Pagination,
  FormControl, InputLabel, Select, MenuItem,
  Alert, CircularProgress, InputAdornment, Tooltip,
  Dialog, DialogTitle, DialogContent, DialogActions,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import SaveIcon from '@mui/icons-material/Save';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { useNavigate } from 'react-router-dom';
import { getAuthPermissoes } from '../services/authStorage';
import {
  getAllProducts,
  createStockEntry,
  createStockExit,
  inventoryAdjust,
  getEstabelecimentos,
  getLocaisEstoque,
  getConfiguracao,
  getAllCategories,
  getSubCategoriesByCategory,
  getProductsBySubCategory,
} from '../services/api';
import { getApiErrorMessage } from '../utils/apiError';
import useModulosLicenca from '../hooks/useModulosLicenca';
import { normalizeSearchTerm, PRODUCT_SEARCH_HINT, fatorCodigoAdicional, processarBuscaProdutoEnter, termoBuscaApi } from '../utils/productSearch';
import { moverItemLista, ordenarListaPor } from '../utils/listOrder';
import ResponsiveTable from './common/ResponsiveTable';

const zen = {
  blue: '#1976d2',
  blueDark: '#155fa0',
  sectionTitleSx: {
    background: '#1976d2',
    color: '#fff',
    fontWeight: 800, letterSpacing: 0.3, px: 2, py: 1,
    borderTopLeftRadius: 10, borderTopRightRadius: 10,
  },
  sectionCardSx: {
    borderRadius: 2, border: '1px solid rgba(0,0,0,0.08)',
    boxShadow: '0 2px 10px rgba(0,0,0,0.04)', backgroundColor: 'background.paper'
  },
};
/// `grow` faz a seção esticar e o conteúdo rolar internamente, para o
/// formulário preencher a altura da janela flutuante sem sobra embaixo.
const Section = ({ title, children, grow = false, sx }) => (
  <Box
    sx={{
      ...zen.sectionCardSx,
      ...(grow ? { display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 } : {}),
      ...sx,
    }}
  >
    <Box sx={zen.sectionTitleSx}>{title}</Box>
    <Box
      sx={{
        p: 2,
        ...(grow
          ? { flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }
          : {}),
      }}
    >
      {children}
    </Box>
  </Box>
);

// helpers
const fmtQtd = (n) => {
  const x = Number(n) || 0;
  return Number.isInteger(x) ? String(x) : x.toFixed(3);
};

export default function FormStockMovement({
  embedded = false,
  onSaved,
  onCancel,
}) {
  const navigate = useNavigate();
  const { isAtivo } = useModulosLicenca();
  const multiEstoque = isAtivo('ADD_MULTI_ESTOQUE');
  const permissions = (() => {
    try {
      return getAuthPermissoes();
    } catch {
      return null;
    }
  })();
  const podeMovimentar = permissions == null || permissions.movimentaEstoque;
  const podeInventariar = permissions == null || permissions.inventarioEstoque;

  // tipo: entrada | saida | inventario
  const [tipo, setTipo] = useState(
    podeMovimentar ? 'entrada' : (podeInventariar ? 'inventario' : 'entrada')
  );
  const [bloquearNegativo, setBloquearNegativo] = useState(true);
  const [permitirEstoqueNegativo, setPermitirEstoqueNegativo] = useState(false);
  const [dlgConfirmNegativo, setDlgConfirmNegativo] = useState(false);
  const [observacao, setObservacao] = useState('');
  const [filiais, setFiliais] = useState([]);
  const [estabMovimentoId, setEstabMovimentoId] = useState('');
  const [localEstoqueId, setLocalEstoqueId] = useState('');
  const [locaisEstoque, setLocaisEstoque] = useState([]);
  const fileInputRef = React.useRef(null);

  // busca
  const [searchText, setSearchText] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [buscando, setBuscando] = useState(false);
  const [resultados, setResultados] = useState([]);
  const [totalPaginas, setTotalPaginas] = useState(1);
  const [totalItens, setTotalItens] = useState(0);
  const [pagina, setPagina] = useState(1);

  const [itens, setItens] = useState([]);
  const [categorias, setCategorias] = useState([]);
  const [subcategorias, setSubcategorias] = useState([]);
  const [categoriaGrupoId, setCategoriaGrupoId] = useState('');
  const [subcategoriaGrupoId, setSubcategoriaGrupoId] = useState('');
  const [qtdGrupoPadrao, setQtdGrupoPadrao] = useState('1');
  const [qtdMassa, setQtdMassa] = useState('');
  const [carregandoGrupo, setCarregandoGrupo] = useState(false);

  // ui
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const fechar = useCallback(() => {
    if (onCancel) onCancel();
    else navigate('/stock-movement');
  }, [onCancel, navigate]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await getEstabelecimentos();
        const lista = Array.isArray(data) ? data : (data ? [data] : []);
        setFiliais(lista.map((e) => ({
          id: e.id,
          nome: e.nomeFantasia || e.razaoSocial || `Filial ${e.id}`,
        })));
        if (lista.length === 1) setEstabMovimentoId(String(lista[0].id));
      } catch {
        // silencioso
      }
    })();
  }, []);

  useEffect(() => {
    const estabCfg = Number(estabMovimentoId) || filiais[0]?.id;
    if (!estabCfg) return;
    getConfiguracao(estabCfg)
      .then(({ data }) => {
        const permitir = Boolean(data?.permitirEstoqueNegativoGerencial);
        setPermitirEstoqueNegativo(permitir);
        setBloquearNegativo(!permitir);
      })
      .catch(() => {
        setPermitirEstoqueNegativo(false);
        setBloquearNegativo(true);
      });
  }, [estabMovimentoId, filiais]);

  useEffect(() => {
    const estab = Number(estabMovimentoId);
    if (!estab) {
      setLocaisEstoque([]);
      setLocalEstoqueId('');
      return undefined;
    }
    let ativo = true;
    getLocaisEstoque(estab)
      .then(({ data }) => {
        if (!ativo) return;
        const lista = (Array.isArray(data) ? data : []).filter((l) => l.ativo !== false);
        const visiveis = multiEstoque ? lista : lista.filter((l) => l.principal);
        setLocaisEstoque(visiveis.length ? visiveis : lista.filter((l) => l.principal));
        const principal = (visiveis.find((l) => l.principal) || lista.find((l) => l.principal) || lista[0]);
        setLocalEstoqueId(principal ? String(principal.id) : '');
      })
      .catch(() => {
        if (!ativo) return;
        setLocaisEstoque([]);
        setLocalEstoqueId('');
      });
    return () => { ativo = false; };
  }, [estabMovimentoId, multiEstoque]);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await getAllCategories({ indice: 1, qtde: 500 });
        setCategorias(data.items || data || []);
      } catch {
        setCategorias([]);
      }
    })();
  }, []);

  useEffect(() => {
    if (!categoriaGrupoId) {
      setSubcategorias([]);
      setSubcategoriaGrupoId('');
      return;
    }
    (async () => {
      try {
        const { data } = await getSubCategoriesByCategory(categoriaGrupoId);
        setSubcategorias(Array.isArray(data) ? data : (data?.items || []));
      } catch {
        setSubcategorias([]);
      }
    })();
  }, [categoriaGrupoId]);

  // buscar produtos
  const buscarProdutos = useCallback(async () => {
    if (!searchTerm?.trim()) {
      setResultados([]); setTotalPaginas(1); setTotalItens(0); return;
    }
    try {
      setBuscando(true);
      const resp = await getAllProducts({
        indice: pagina,
        qtde: 10,
        termo: normalizeSearchTerm(searchTerm),
        estabelecimentoId: estabMovimentoId || '',
      });
      const data = resp.data;
      setResultados(data.items || []);
      setTotalPaginas(data.totalPages || 1);
      setTotalItens(data.totalItems || 0);
    } catch (err) {
      setError(getApiErrorMessage(err, 'Erro ao buscar produtos.'));
    } finally { setBuscando(false); }
  }, [searchTerm, pagina, estabMovimentoId]);

  useEffect(() => { buscarProdutos(); }, [buscarProdutos]);
  useEffect(() => { setPagina(1); }, [searchTerm]);

  const confirmarBusca = () => setSearchTerm(searchText);
  const onSearchKeyDown = async (e) => {
    if (e.key !== 'Enter') return;
    e.preventDefault();
    if (!termoBuscaApi(searchText)) {
      confirmarBusca();
      return;
    }
    try {
      setBuscando(true);
      setError('');
      const resultado = await processarBuscaProdutoEnter({
        termo: searchText,
        estabelecimentoId: estabMovimentoId,
        qtde: 20,
      });
      if (resultado.tipo === 'adicionar') {
        adicionar(resultado.produto, resultado.quantidade);
        setSearchText('');
        setSearchTerm('');
        setResultados([]);
        setTotalPaginas(1);
        setTotalItens(0);
        return;
      }
      if (resultado.tipo === 'nao_encontrado') {
        setError(resultado.mensagem);
        setResultados([]);
        return;
      }
      if (resultado.tipo === 'lista') {
        setSearchTerm(searchText);
        setResultados(resultado.produtos);
        setTotalPaginas(1);
        setTotalItens(resultado.produtos.length);
        setPagina(1);
      }
    } catch (err) {
      setError(getApiErrorMessage(err, 'Erro ao buscar produtos.'));
    } finally {
      setBuscando(false);
    }
  };

  // manipulação de itens
  const adicionar = (p, qtd = 1, dataContagem = null) => {
    setItens(prev => {
      const exist = prev.find(i => i.produtoId === p.id);
      const agora = dataContagem || new Date().toISOString();
      if (exist) {
        return prev.map(i => i.produtoId === p.id
          ? {
              ...i,
              quantidade: Number(i.quantidade) + Number(qtd),
              // Mantém a data da primeira inserção na lista.
              dataContagem: i.dataContagem || (tipo === 'inventario' ? agora : i.dataContagem),
            }
          : i);
      }
      return [...prev, {
        produtoId: p.id,
        codigo: p.codigo,
        ean: p.ean,
        descricao: p.descricao,
        categoriaNome: p.categoriaNome || '',
        estoqueAtual: Number(p.estoqueAtual || 0),
        quantidade: Number(qtd) || 1,
        custo: '',
        venda: '',
        dataContagem: tipo === 'inventario' ? agora : null,
      }];
    });
  };
  const remover = (id) => setItens(prev => prev.filter(i => i.produtoId !== id));
  const limparLista = () => setItens([]);
  const moverItem = (index, delta) => {
    setItens((prev) => moverItemLista(prev, index, delta));
  };
  const ordenarItens = (modo) => {
    setItens((prev) => {
      if (modo === 'grupo') {
        return [...prev].sort((a, b) => {
          const ga = String(a.categoriaNome || '').toLocaleLowerCase('pt-BR');
          const gb = String(b.categoriaNome || '').toLocaleLowerCase('pt-BR');
          const cmp = ga.localeCompare(gb, 'pt-BR');
          if (cmp !== 0) return cmp;
          return String(a.descricao || '').localeCompare(String(b.descricao || ''), 'pt-BR');
        });
      }
      if (modo === 'codigo') {
        return ordenarListaPor(prev, 'codigo', 'asc');
      }
      if (modo === 'produto-desc') {
        return ordenarListaPor(prev, 'descricao', 'desc');
      }
      return ordenarListaPor(prev, 'descricao', 'asc');
    });
  };
  const aplicarQtdMassa = () => {
    const q = Number(String(qtdMassa).replace(',', '.'));
    if (!Number.isFinite(q) || q < 0) {
      setError('Informe uma quantidade válida para aplicar em massa.');
      return;
    }
    setError('');
    setItens((prev) => prev.map((i) => ({ ...i, quantidade: q })));
  };
  const adicionarPorGrupo = async () => {
    if (!categoriaGrupoId && !subcategoriaGrupoId) {
      setError('Selecione um grupo (categoria) ou subgrupo.');
      return;
    }
    const qtdPadrao = Number(String(qtdGrupoPadrao).replace(',', '.'));
    if (!Number.isFinite(qtdPadrao) || qtdPadrao < 0) {
      setError('Quantidade padrão inválida.');
      return;
    }
    setError('');
    setCarregandoGrupo(true);
    try {
      let produtos = [];
      if (subcategoriaGrupoId) {
        const { data } = await getProductsBySubCategory(subcategoriaGrupoId);
        produtos = Array.isArray(data) ? data : (data?.items || []);
      } else {
        let pagina = 1;
        while (true) {
          const { data } = await getAllProducts({
            indice: pagina,
            qtde: 100,
            categoriaId: categoriaGrupoId,
            estabelecimentoId: estabMovimentoId || '',
          });
          produtos.push(...(data.items || []));
          if (pagina >= (data.totalPages || 1)) break;
          pagina += 1;
        }
      }
      if (!produtos.length) {
        setError('Nenhum produto encontrado no grupo selecionado.');
        return;
      }
      const agora = new Date().toISOString();
      setItens((prev) => {
        const mapa = new Map(prev.map((i) => [i.produtoId, { ...i }]));
        produtos.forEach((p) => {
          const exist = mapa.get(p.id);
          if (exist) {
            mapa.set(p.id, {
              ...exist,
              quantidade: Number(exist.quantidade) + qtdPadrao,
            });
          } else {
            mapa.set(p.id, {
              produtoId: p.id,
              codigo: p.codigo,
              ean: p.ean,
              descricao: p.descricao,
              categoriaNome: p.categoriaNome || '',
              estoqueAtual: Number(p.estoqueAtual || 0),
              quantidade: qtdPadrao,
              custo: '',
              venda: '',
              dataContagem: tipo === 'inventario' ? agora : null,
            });
          }
        });
        return Array.from(mapa.values());
      });
    } catch (err) {
      setError(getApiErrorMessage(err, 'Falha ao carregar produtos do grupo.'));
    } finally {
      setCarregandoGrupo(false);
    }
  };
  const inc = (id) => setItens(prev => prev.map(i => i.produtoId === id
    ? {
        ...i,
        quantidade: Number(i.quantidade) + 1,
      }
    : i));
  const dec = (id) => setItens(prev => prev.map(i => i.produtoId === id
    ? {
        ...i,
        quantidade: Math.max(0, Number(i.quantidade) - 1),
      }
    : i));
  const setField = (id, field, value) =>
    setItens(prev => prev.map(i => i.produtoId === id
      ? { ...i, [field]: value }
      : i));

  /// Importa coletor CSV no formato do ZenPDV: codigo;qtde[;datahora]
  const importarColetor = async (file) => {
    if (!file || tipo !== 'inventario') return;
    try {
      setError('');
      const text = await file.text();
      const linhas = text.split(/\r?\n/).filter((l) => l.trim());
      if (linhas.length <= 1) {
        setError('Arquivo CSV sem dados. Formato: codigo;qtde');
        return;
      }
      const mapa = new Map();
      for (let i = 1; i < linhas.length; i++) {
        const cols = linhas[i].split(';').map((c) => c.trim());
        if (cols.length < 2) continue;
        const codigo = cols[0];
        const qtde = Number(String(cols[1]).replace(',', '.'));
        if (!codigo || !Number.isFinite(qtde) || qtde < 0) continue;
        const data = cols[2] ? new Date(cols[2]) : new Date();
        const dataIso = Number.isNaN(data.getTime())
          ? new Date().toISOString()
          : data.toISOString();
        const prev = mapa.get(codigo) || { quantidade: 0, dataContagem: dataIso };
        mapa.set(codigo, {
          quantidade: prev.quantidade + qtde,
          dataContagem: prev.dataContagem <= dataIso ? prev.dataContagem : dataIso,
        });
      }
      if (mapa.size === 0) {
        setError('Nenhuma linha válida no CSV (codigo;qtde).');
        return;
      }

      for (const [codigo, info] of mapa.entries()) {
        const resp = await getAllProducts({ indice: 1, qtde: 5, termo: codigo });
        const produto = (resp.data?.items || []).find(
          (p) =>
            String(p.codigo || '').toLowerCase() === codigo.toLowerCase() ||
            String(p.ean || '').toLowerCase() === codigo.toLowerCase() ||
            (p.codigosAdicionais || []).some(
              (c) => String(c.codigo || '').toLowerCase() === codigo.toLowerCase()
            )
        );
        if (!produto) {
          setError((e) => (e ? `${e}\n` : '') + `Produto '${codigo}' não encontrado.`);
          continue;
        }
        const qtd = Number(info.quantidade) * fatorCodigoAdicional(produto, codigo);
        setItens((prev) => {
          const exist = prev.find((i) => i.produtoId === produto.id);
          if (exist) {
            return prev.map((i) =>
              i.produtoId === produto.id
                ? {
                    ...i,
                    quantidade: Number(qtd),
                    dataContagem: info.dataContagem,
                  }
                : i
            );
          }
          return [
            ...prev,
            {
              produtoId: produto.id,
              codigo: produto.codigo,
              ean: produto.ean,
              descricao: produto.descricao,
              categoriaNome: produto.categoriaNome || '',
              estoqueAtual: Number(produto.estoqueAtual || 0),
              quantidade: Number(qtd),
              dataContagem: info.dataContagem,
            },
          ];
        });
      }
    } catch (err) {
      setError(getApiErrorMessage(err, 'Falha ao importar coletor.'));
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  // validação
  const podeSalvar = useMemo(() => {
    if (filiais.length > 0 && !Number(estabMovimentoId)) return false;
    if (itens.length === 0) return false;
    for (const it of itens) {
      const q = Number(it.quantidade);
      if (!Number.isFinite(q) || q < 0) return false;
      if (tipo === 'saida' && !permitirEstoqueNegativo && bloquearNegativo && it.estoqueAtual - q < 0) return false;
    }
    return true;
  }, [itens, tipo, bloquearNegativo, permitirEstoqueNegativo, filiais.length, estabMovimentoId]);

  const itensSaidaNegativos = useMemo(() => {
    if (tipo !== 'saida') return [];
    return itens.filter((it) => {
      const q = Number(it.quantidade);
      return Number.isFinite(q) && q > 0 && it.estoqueAtual - q < 0;
    });
  }, [itens, tipo]);

  // saldo pós
  const saldoPos = (it) => {
    const q = Number(it.quantidade) || 0;
    const a = Number(it.estoqueAtual) || 0;
    if (tipo === 'inventario') return q;
    return tipo === 'entrada' ? a + q : a - q;
  };

  // salvar
  const salvar = async (forcarNegativo = false) => {
    setError('');
    if (!podeSalvar) {
      setError('Preencha os itens corretamente.');
      return;
    }
    if (tipo === 'saida' && !forcarNegativo && permitirEstoqueNegativo && itensSaidaNegativos.length > 0) {
      setDlgConfirmNegativo(true);
      return;
    }
    const estabId = Number(estabMovimentoId) || 1;
    const bloquear = tipo === 'saida' ? (forcarNegativo ? false : bloquearNegativo) : true;
    try {
      setSaving(true);

      const itensPayload = itens.map(i => ({
        produtoId: i.produtoId,
        quantidade: Number(i.quantidade),
        estabelecimentoId: estabId,
        localEstoqueId: Number(localEstoqueId) || 0,
        ...(observacao ? { observacao } : {}),
        ...(tipo === 'inventario'
          ? { dataContagem: i.dataContagem || new Date().toISOString() }
          : {}),
      }));

      if (tipo === 'entrada') {
        if (!podeMovimentar) throw new Error('Sem permissão para movimentar estoque.');
        await createStockEntry(itensPayload);
      } else if (tipo === 'saida') {
        if (!podeMovimentar) throw new Error('Sem permissão para movimentar estoque.');
        await createStockExit(itensPayload, bloquear);
      } else {
        if (!podeInventariar) throw new Error('Sem permissão para inventário.');
        await inventoryAdjust(itensPayload);
      }

      setDlgConfirmNegativo(false);
      if (onSaved) onSaved('Movimentação registrada');
      else navigate('/stock-movement');
    } catch (err) {
      setError(getApiErrorMessage(err, 'Falha ao salvar movimento.'));
    } finally { setSaving(false); }
  };

  const colQtdTitulo = tipo === 'inventario' ? 'Novo saldo' : 'Qtd';

  // hotkeys
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'F10') { e.preventDefault(); document.getElementById('btn-save-stock')?.click(); }
      else if (e.key === 'Escape' && !embedded) { e.preventDefault(); fechar(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [embedded, fechar]);

  return (
    <Box
      sx={{
        p: embedded ? 0 : 3,
        maxWidth: embedded ? 'none' : 1400,
        mx: 'auto',
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        // No desktop o formulário preenche a janela; no mobile volta a rolar.
        ...(embedded ? { flex: { xs: '0 0 auto', md: 1 } } : {}),
      }}
    >
      {!embedded && (
        <Typography variant="h5" sx={{ textAlign: 'center', color: zen.blue, fontWeight: 900, mb: 2, letterSpacing: 0.5 }}>
          Novo Movimento de Estoque
        </Typography>
      )}

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Grid
        container
        spacing={2}
        sx={{ flex: { xs: '0 0 auto', md: 1 }, minHeight: 0, alignItems: 'stretch' }}
      >
        {/* esquerda: busca */}
        <Grid item xs={12} md={6} sx={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
          <Section title="Buscar Produto" grow>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1, flexWrap: 'wrap' }}>
              <TextField
                size="small" fullWidth
                placeholder={PRODUCT_SEARCH_HINT}
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                onKeyDown={onSearchKeyDown}
                InputProps={{
                  startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>,
                  endAdornment: searchText ? (
                    <InputAdornment position="end">
                      <IconButton size="small" onClick={() => setSearchText('')}>
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ) : null
                }}
              />
              <Button variant="contained" onClick={() => setSearchTerm(searchText)}>Buscar</Button>
            </Stack>

            <ResponsiveTable sx={{ flex: 1, minHeight: 120, overflowY: 'auto' }}>
              <Table size="small" stickyHeader sx={{ tableLayout: 'fixed' }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Produto</TableCell>
                    <TableCell sx={{ width: 130 }}>Categoria</TableCell>
                    <TableCell align="right" sx={{ width: 80 }}>Estoque</TableCell>
                    <TableCell align="center" sx={{ width: 56 }} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {buscando ? (
                    <TableRow><TableCell colSpan={4} align="center"><CircularProgress size={20} /></TableCell></TableRow>
                  ) : resultados.length ? (
                    resultados.map((p) => (
                      <TableRow key={p.id} hover>
                        <TableCell data-label="Produto" sx={{ overflow: 'hidden' }}>
                          <Typography variant="body2" noWrap title={p.descricao}>{p.descricao}</Typography>
                          <Typography variant="caption" color="text.secondary" noWrap component="div">
                            {p.codigo || '-'}{p.ean ? ` · ${p.ean}` : ''}
                          </Typography>
                        </TableCell>
                        <TableCell data-label="Categoria" sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                          {p.categoriaNome || '-'}
                        </TableCell>
                        <TableCell align="right" data-label="Estoque">{fmtQtd(p.estoqueAtual)}</TableCell>
                        <TableCell align="center" data-actions="">
                          <Tooltip title="Adicionar">
                            <IconButton size="small" color="primary" onClick={() => adicionar(p, fatorCodigoAdicional(p, searchTerm))}>
                              <AddIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <TableRow><TableCell colSpan={4} align="center">Sem resultados</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </ResponsiveTable>

            <Stack direction="row" spacing={1} alignItems="center" justifyContent="center" sx={{ mt: 1, flexShrink: 0 }}>
              <Pagination size="small" count={totalPaginas} page={pagina} onChange={(_e, p) => setPagina(p)} />
              <Typography variant="caption">Exibindo {resultados.length} de {totalItens}</Typography>
            </Stack>
          </Section>

          <Section title="Adicionar por grupo" sx={{ mt: 2 }}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small">
                  <InputLabel>Grupo (categoria)</InputLabel>
                  <Select
                    label="Grupo (categoria)"
                    value={categoriaGrupoId}
                    onChange={(e) => {
                      setCategoriaGrupoId(e.target.value);
                      setSubcategoriaGrupoId('');
                    }}
                  >
                    <MenuItem value=""><em>Selecione</em></MenuItem>
                    {categorias.map((c) => (
                      <MenuItem key={c.id} value={String(c.id)}>{c.descricao || c.nome}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth size="small" disabled={!categoriaGrupoId}>
                  <InputLabel>Subgrupo (opcional)</InputLabel>
                  <Select
                    label="Subgrupo (opcional)"
                    value={subcategoriaGrupoId}
                    onChange={(e) => setSubcategoriaGrupoId(e.target.value)}
                  >
                    <MenuItem value=""><em>Todos do grupo</em></MenuItem>
                    {subcategorias.map((s) => (
                      <MenuItem key={s.id} value={String(s.id)}>{s.descricao || s.nome}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  label="Qtd padrão"
                  size="small"
                  fullWidth
                  type="number"
                  value={qtdGrupoPadrao}
                  onChange={(e) => setQtdGrupoPadrao(e.target.value)}
                  inputProps={{ min: 0, step: 'any' }}
                />
              </Grid>
              <Grid item xs={12} sm={8} sx={{ display: 'flex', alignItems: 'center' }}>
                <Button
                  variant="contained"
                  fullWidth
                  disabled={carregandoGrupo || (!categoriaGrupoId && !subcategoriaGrupoId)}
                  onClick={adicionarPorGrupo}
                >
                  {carregandoGrupo ? 'Carregando...' : 'Adicionar todos do grupo'}
                </Button>
              </Grid>
            </Grid>
          </Section>
        </Grid>

        {/* direita: config + itens */}
        <Grid item xs={12} md={6} sx={{ display: 'flex', flexDirection: 'column', gap: 2, minHeight: 0 }}>
          <Section title="Configuração">
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <FormControl fullWidth size="small">
                  <InputLabel>Tipo</InputLabel>
                  <Select label="Tipo" value={tipo} onChange={(e) => setTipo(e.target.value)}>
                    {podeMovimentar && <MenuItem value="entrada">Entrada (Ajuste +)</MenuItem>}
                    {podeMovimentar && <MenuItem value="saida">Saída (Ajuste -)</MenuItem>}
                    {podeInventariar && <MenuItem value="inventario">Inventário (define saldo)</MenuItem>}
                  </Select>
                </FormControl>
              </Grid>
              {filiais.length > 0 && (
                <Grid item xs={12}>
                  <FormControl fullWidth size="small" required>
                    <InputLabel>Filial</InputLabel>
                    <Select
                      label="Filial"
                      value={estabMovimentoId}
                      onChange={(e) => setEstabMovimentoId(e.target.value)}
                    >
                      {filiais.map((f) => (
                        <MenuItem key={f.id} value={String(f.id)}>{f.nome}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              )}
              {multiEstoque && locaisEstoque.length > 0 && (
                <Grid item xs={12}>
                  <FormControl fullWidth size="small" required>
                    <InputLabel>Local</InputLabel>
                    <Select
                      label="Local"
                      value={localEstoqueId}
                      onChange={(e) => setLocalEstoqueId(e.target.value)}
                    >
                      {locaisEstoque.map((l) => (
                        <MenuItem key={l.id} value={String(l.id)}>
                          {l.nome}{l.principal ? ' (principal)' : ''}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              )}
              {tipo === 'inventario' && (
                <Grid item xs={12}>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".csv,text/csv"
                    hidden
                    onChange={(e) => importarColetor(e.target.files?.[0])}
                  />
                  <Button
                    variant="outlined"
                    startIcon={<UploadFileIcon />}
                    onClick={() => fileInputRef.current?.click()}
                    fullWidth
                  >
                    Importar coletor (codigo;qtde)
                  </Button>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 0.5 }}>
                    A data/hora de cada item é a da inserção na lista. Vendas posteriores são abatidas.
                    Coletor opcional: codigo;qtde;datahora.
                  </Typography>
                </Grid>
              )}
              {tipo === 'saida' && !permitirEstoqueNegativo && (
                <Grid item xs={12}>
                  <Typography variant="caption" color="text.secondary">
                    Saídas que deixariam o estoque negativo são bloqueadas. Ative a opção nas configurações do estabelecimento se precisar permitir com confirmação.
                  </Typography>
                </Grid>
              )}
                <Grid item xs={12}>
                  <TextField
                    label="Observação (opcional)"
                    value={observacao}
                    onChange={(e) => setObservacao(e.target.value)}
                    fullWidth multiline minRows={2} maxRows={4} size="small"
                  />
                </Grid>
            </Grid>
          </Section>

          <Section title={`Itens do movimento (${itens.length})`} grow>
            {itens.length > 0 && (
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={1}
                alignItems={{ sm: 'center' }}
                sx={{ mb: 1, flexShrink: 0 }}
              >
                <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
                  Ordenar:
                </Typography>
                <Button size="small" variant="outlined" onClick={() => ordenarItens('produto')}>
                  Por produto (A-Z)
                </Button>
                <Button size="small" variant="outlined" onClick={() => ordenarItens('grupo')}>
                  Por grupo
                </Button>
                <Button size="small" variant="outlined" onClick={() => ordenarItens('codigo')}>
                  Por código
                </Button>
                <TextField
                  size="small"
                  label="Qtd p/ todos"
                  type="number"
                  value={qtdMassa}
                  onChange={(e) => setQtdMassa(e.target.value)}
                  inputProps={{ min: 0, step: 'any', style: { width: 72 } }}
                  sx={{ width: 120 }}
                />
                <Button size="small" variant="contained" onClick={aplicarQtdMassa}>
                  Aplicar
                </Button>
              </Stack>
            )}
            <ResponsiveTable sx={{ flex: 1, minHeight: 120, overflowY: 'auto' }}>
              <Table size="small" stickyHeader sx={{ tableLayout: 'fixed' }}>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ width: 56 }} />
                    <TableCell>Produto</TableCell>
                    <TableCell align="right" sx={{ width: 72 }}>Estoque</TableCell>
                    <TableCell align="center" sx={{ width: 124 }}>{colQtdTitulo}</TableCell>
                    <TableCell align="right" sx={{ width: 66 }}>Pós</TableCell>
                    <TableCell align="center" sx={{ width: 52 }} />
                  </TableRow>
                </TableHead>
                <TableBody>
                  {itens.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={6} align="center">Nenhum item</TableCell>
                    </TableRow>
                  ) : itens.map((it, index) => (
                    <TableRow key={it.produtoId} hover>
                      <TableCell data-label="Ordem">
                        <Stack direction="row" spacing={0}>
                          <IconButton
                            size="small"
                            sx={{ p: 0.25 }}
                            disabled={index === 0}
                            onClick={() => moverItem(index, -1)}
                            aria-label="Subir"
                          >
                            <ArrowUpwardIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            sx={{ p: 0.25 }}
                            disabled={index === itens.length - 1}
                            onClick={() => moverItem(index, 1)}
                            aria-label="Descer"
                          >
                            <ArrowDownwardIcon fontSize="small" />
                          </IconButton>
                        </Stack>
                      </TableCell>
                      <TableCell data-label="Produto" sx={{ overflow: 'hidden' }}>
                        <Typography variant="body2" noWrap title={it.descricao}>{it.descricao}</Typography>
                        <Typography variant="caption" color="text.secondary" noWrap component="div">
                          {it.codigo || '-'}
                          {it.categoriaNome ? ` · ${it.categoriaNome}` : ''}
                          {tipo === 'inventario' && it.dataContagem
                            ? ` · contado ${new Date(it.dataContagem).toLocaleString('pt-BR')}`
                            : ''}
                        </Typography>
                      </TableCell>
                      <TableCell align="right" data-label="Estoque">{fmtQtd(it.estoqueAtual)}</TableCell>
                      <TableCell align="center" data-label={colQtdTitulo}>
                        <Stack direction="row" spacing={0} alignItems="center" justifyContent="center" sx={{ whiteSpace: 'nowrap' }}>
                          <IconButton size="small" sx={{ p: 0.25 }} onClick={() => dec(it.produtoId)}>
                            <RemoveIcon fontSize="small" />
                          </IconButton>
                          <TextField
                            size="small" type="number" value={it.quantidade}
                            onChange={(e) => setField(it.produtoId, 'quantidade', e.target.value)}
                            inputProps={{ min: 0, step: 'any', style: { textAlign: 'center', width: 52, padding: '6px 2px' } }}
                          />
                          <IconButton size="small" sx={{ p: 0.25 }} onClick={() => inc(it.produtoId)}>
                            <AddIcon fontSize="small" />
                          </IconButton>
                        </Stack>
                      </TableCell>

                      <TableCell align="right" data-label="Pós">{fmtQtd(saldoPos(it))}</TableCell>
                      <TableCell align="center" data-actions="">
                        <Tooltip title="Remover">
                          <IconButton size="small" color="error" onClick={() => remover(it.produtoId)}>
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </ResponsiveTable>

            {itens.length > 0 && (
              <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 1, flexShrink: 0 }}>
                <Button size="small" variant="text" color="inherit" startIcon={<ClearIcon />} onClick={limparLista}>
                  Limpar lista
                </Button>
              </Stack>
            )}
          </Section>
        </Grid>
      </Grid>

      <Box sx={{ mt: 2, display: 'flex', gap: 1, justifyContent: 'center', flexWrap: 'wrap', flexShrink: 0 }}>
        <Button
          id="btn-save-stock"
          variant="contained"
          startIcon={<SaveIcon />}
          disabled={!podeSalvar || saving}
          onClick={() => salvar(false)}
          sx={{ minWidth: 160, background: zen.blue, '&:hover': { background: zen.blueDark }, textTransform: 'none', fontWeight: 700 }}
        >
          Gravar (F10)
        </Button>
        <Button
          variant="contained" color="inherit" startIcon={<ArrowBackIcon />} onClick={fechar}
          sx={{ minWidth: 160, textTransform: 'none', fontWeight: 700 }}
        >
          Voltar{embedded ? '' : ' (ESC)'}
        </Button>
      </Box>

      <Dialog open={dlgConfirmNegativo} onClose={() => !saving && setDlgConfirmNegativo(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Estoque ficará negativo</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mb: 2 }}>
            A saída deixará o saldo negativo para os itens abaixo. Deseja continuar mesmo assim?
          </Typography>
          <Stack spacing={1}>
            {itensSaidaNegativos.map((it) => (
              <Typography key={it.produtoId} variant="body2">
                {it.descricao} — saldo {fmtQtd(it.estoqueAtual)}, sair {fmtQtd(it.quantidade)}
              </Typography>
            ))}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDlgConfirmNegativo(false)} disabled={saving}>Não</Button>
          <Button variant="contained" color="warning" onClick={() => salvar(true)} disabled={saving}>
            Sim, continuar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
